import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { getCookie } from "cookies-next";
import PageTemplate from "@/components/templates/PageTemplate";
import { serverSideTranslations } from "@/libs/i18n/serverSideTranslations";
import { COUNTRY_COOKIE_NAME } from "@/libs/consent/gdprRegions";
import { useTrans } from "@/utils/useTrans";
import { cn } from "@/libs/utils";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useBottomSheet } from "@/v2-components/pages/where-to-try/_hooks/useBottomSheet";
import type { ArcadeMapHandle } from "@/v2-components/pages/where-to-try/_components/ArcadeMap";
import { analyticsInstance } from "@/libs/analytics";
import arcadeData from "../../../data/arcade-locations.json";
import {
  ArcadeDataFile,
  countriesWithVenues,
  countryCentroid,
  flattenLocations,
  haversineKm,
  LatLng,
  nearestVenue,
  partnersWithVenues,
} from "@/v2-components/pages/where-to-try/_constants/where-to-try-constants";
import ArcadeMap from "@/v2-components/pages/where-to-try/_components/ArcadeMap";
import LocationList from "@/v2-components/pages/where-to-try/_components/LocationList";
import PartnerFilter from "@/v2-components/pages/where-to-try/_components/PartnerFilter";
import HeroSection from "@/v2-components/pages/where-to-try/_components/HeroSection";
import LocationNoticeBanner from "@/v2-components/pages/where-to-try/_components/LocationNoticeBanner";

// 공개 노출 파트너 화이트리스트. **JSON 에 partner 를 추가해도 여기 id 를 넣지 않으면 페이지에
// 아무것도 안 나온다**(빌드 에러·경고 없이 조용히). Hologate / VEX / Levels 는 venue 단위로
// bHaptics 사용이 확인되지 않아 데이터만 보존 중.
// 파트너별 등재 사유는 arcade-locations.json 의 `_source` · `description` 이 정본.
const EXPOSED_PARTNER_IDS = ["sandbox-vr", "eventop"];

const CC_RE = /^[A-Z]{2}$/;
const LS_KEY = "wte_cc"; // 직전에 사용자가 고른 국가(ISO-2)

interface Props {
  data: ArcadeDataFile;
}

export default function WhereToTryPage({ data }: Props) {
  const router = useRouter();
  const { t } = useTrans(["where-to-try"]);

  // 국가는 hydration 이후에만 해소한다(서버=중립, country=null). 렌더 중 cookie/localStorage/
  // window 를 읽으면 hydration mismatch → 서버·첫 클라 렌더는 반드시 country=null 로 동일.
  const [country, setCountry] = useState<string | null>(null);
  const [countryAuto, setCountryAuto] = useState(true);
  // userLoc = "Use my location" 로 얻은 정밀 좌표(단일). ephemeral — 절대 persist 금지.
  const [userLoc, setUserLoc] = useState<LatLng | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [partnerFilter, setPartnerFilter] = useState<string | null>(null);
  // "Choose another country" 클릭 시 콤보박스를 열기 위한 신호(증가 토큰) — 프로토 openMenu.
  const [countryPickerToken, setCountryPickerToken] = useState(0);

  const resolvedRef = useRef(false);
  const filterRef = useRef<HTMLDivElement | null>(null);
  const mapHandle = useRef<ArcadeMapHandle | null>(null);

  // 프로토 860px 브레이크포인트 그대로 보존(arbitrary media query). SSR-safe: 마운트 전 false.
  const isMobile = useMediaQuery("(max-width: 860px)");
  // 모바일 풀스크린 지도 모드(프로토 mapFull): 기본 = sticky 지도 밴드 + 리스트 일반 흐름,
  // 밴드 탭 → 풀스크린(고정 오버레이) + 바텀시트. minimize/Escape 로 밴드 복귀.
  const [mapFull, setMapFull] = useState(false);
  // 바텀시트는 풀스크린일 때만 활성 — 밴드 모드에선 시트/재중심 비활성(지도만 fit).
  const {
    snap: sheetSnap,
    setSnap,
    promote: promoteSheet,
    stageRef,
    sheetRef,
    headerRef: sheetHeaderRef,
    contentRef: sheetContentRef,
    sheetStyle,
    onHandlePointerDown,
    contentScrollable,
  } = useBottomSheet({
    enabled: isMobile && mapFull,
    // 시트 높이 변화 → 보던 지점 유지한 채 가시영역 기준 재중심(panBy, refit=뷰 리셋이라 부적절).
    onSnapChange: (_snap, coveredPx) =>
      mapHandle.current?.nudgeForCover(coveredPx),
  });
  const enterMapFull = () => setMapFull(true);
  const exitMapFull = () => {
    setMapFull(false);
    setSnap("half"); // 재진입 시 리스트 보이는 기본 스냅으로 리셋
  };
  // "Choose another country": 콤보박스로 스크롤 + 열기(프로토 openMenu). 토큰 증가로 신호.
  const openCountryPicker = () => {
    filterRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setCountryPickerToken((n) => n + 1);
  };

  // 풀스크린 진입 시 body 스크롤 잠금 + Escape 로 이탈.
  useEffect(() => {
    if (!mapFull) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") exitMapFull();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapFull]);

  // 풀스크린 종료(밴드 복귀)·초기 밴드 시에만 밴드에 맞춰 재핏. 풀스크린 진입 시엔 뷰 보존
  // (시트가 nudgeForCover 로 재중심) — refit(0)은 전체 뷰포트 중심 리셋이라 진입 시 금지.
  useEffect(() => {
    if (!isMobile || mapFull) return;
    const id = setTimeout(() => mapHandle.current?.refit(0), 180);
    return () => clearTimeout(id);
  }, [mapFull, isMobile]);

  const allFlat = useMemo(() => flattenLocations(data), [data]);
  // 모바일 시트 내부 행 ref(선택 시 동기 scrollTop 자동 스크롤 — §7-5).
  const sheetRowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // 매장 보유 국가(노출 venue 기준). 콤보박스 + ?country= 검증에 사용.
  const countries = useMemo(() => countriesWithVenues(allFlat), [allFlat]);
  const countrySet = useMemo(
    () => new Set(countries.map((c) => c.code)),
    [countries],
  );
  const defaultCountry = useMemo(
    () => (countrySet.has("US") ? "US" : (countries[0]?.code ?? null)),
    [countrySet, countries],
  );

  const validCc = (v: unknown): string | null => {
    if (typeof v !== "string") return null;
    const cc = v.toUpperCase();
    return CC_RE.test(cc) && countrySet.has(cc) ? cc : null;
  };

  // 국가 우선 진입(가이드 §7-1): ?country= > localStorage(wte_cc) > 쿠키 bhap_country > US.
  // 마운트 후 1회. router.isReady 게이트(SSG 는 첫 렌더에 query 가 빈값).
  useEffect(() => {
    if (resolvedRef.current || !router.isReady) return;
    resolvedRef.current = true;

    const cookieCc = validCc(getCookie(COUNTRY_COOKIE_NAME));
    let stored: string | null = null;
    try {
      stored = validCc(localStorage.getItem(LS_KEY));
    } catch {
      /* private mode 등 — 무시 */
    }
    const qCc = validCc(router.query.country);
    const chosen = qCc ?? stored ?? cookieCc ?? defaultCountry;

    setCountry(chosen);
    // auto = 해소된 국가가 IP 감지(쿠키)와 같음 → locate 버튼/자동 정렬 힌트. (프로토 initialCountry)
    const auto = chosen != null && chosen === cookieCc;
    setCountryAuto(auto);
    if (chosen) {
      const source = qCc
        ? "deeplink"
        : stored
          ? "stored"
          : cookieCc
            ? "ip"
            : "default";
      analyticsInstance?.whereToTryLocationSet({
        country: chosen,
        auto,
        source,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);

  // ?country= 를 URL 에 반영(사용자 조작 시). locale 프리픽스 유지 위해 객체 형태 shallow replace.
  const writeCountryToUrl = (cc: string) => {
    router.replace(
      { pathname: router.pathname, query: { ...router.query, country: cc } },
      undefined,
      { shallow: true, scroll: false },
    );
  };

  // 국가 전환 단일 진입점. user=true(콤보박스/geo) 면 수동 취급 + URL/localStorage 갱신 + userLoc 리셋.
  const applyCountry = (cc: string, opts?: { user?: boolean }) => {
    setCountry(cc);
    setSelectedId(null);
    if (opts?.user) {
      setCountryAuto(false);
      setUserLoc(null);
      setGeoError(null);
      writeCountryToUrl(cc);
      try {
        localStorage.setItem(LS_KEY, cc);
      } catch {
        /* 무시 */
      }
      analyticsInstance?.whereToTryLocationSet({
        country: cc,
        auto: false,
        source: "select",
      });
    }
  };

  // "Use my location": 정밀 좌표 → 가장 가까운 매장의 국가로 전환 + 그 좌표로 정렬/fly.
  // 권한은 버튼 클릭(유저 제스처)에서만 요청. navigator 접근은 핸들러 안에서만(hydration 무관).
  const useMyLocation = () => {
    setGeoError(null);
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoError("Couldn't get your location — pick a country above.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const loc: LatLng = {
          lat: p.coords.latitude,
          lng: p.coords.longitude,
        };
        const near = nearestVenue(allFlat, loc);
        const nearCc = near?.countryCode?.toUpperCase();
        if (nearCc && countrySet.has(nearCc) && nearCc !== country) {
          applyCountry(nearCc, { user: true });
        }
        setUserLoc(loc);
        setSelectedId(null);
        analyticsInstance?.whereToTryUseMyLocation({
          country: nearCc ?? country,
        });
      },
      () => setGeoError("Couldn't get your location — pick a country above."),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  };

  // 노출 범위: country 해소 전(서버·첫 클라)엔 전 매장(SEO: 전 매장이 HTML에), 이후 해당 국가로 좁힘.
  const scopeVenues = useMemo(
    () =>
      country ? allFlat.filter((l) => l.countryCode === country) : allFlat,
    [allFlat, country],
  );

  // 파트너 탭 옵션 = **현재 국가에 매장이 있는 파트너만**, 그 국가 기준 카운트.
  // 전역 목록을 넘기면 빈 조합을 고를 수 있다 — 예: country=US 에서 Eventop(아르헨티나 1곳)
  // 탭이 보이고, 누르면 교집합 0건이라 아무것도 안 나온다.
  //
  // country 해소 전(SSG HTML·첫 페인트)에는 **빈 배열**을 준다. 그때는 scopeVenues 가 전 매장
  // (SEO 의도)이라 파트너가 2곳으로 잡혀 탭 바가 그려지고, hydration 직후 국가가 해소되면
  // 1곳이 되어 사라진다 → 약 57px 레이아웃 시프트 + 그 창에 탭을 누르면 빈 리스트.
  const scopePartners = useMemo(
    () => (country ? partnersWithVenues(data.partners, scopeVenues) : []),
    [country, data.partners, scopeVenues],
  );

  // 국가를 바꿔 선택 파트너가 범위 밖이 되면 필터를 무효로 본다(빈 리스트 방지).
  // applyCountry 에서 setPartnerFilter(null) 로 지우는 대안은 최초 국가 해소가 applyCountry 가
  // 아니라 마운트 effect 의 setCountry 라서 정리 지점이 2곳으로 늘어난다 → 파생이 더 싸다.
  // ⚠️ 렌더에는 partnerFilter 대신 **항상 이 값**을 쓸 것(raw 는 setState·analytics 용).
  const effectivePartnerFilter =
    partnerFilter && scopePartners.some((p) => p.id === partnerFilter)
      ? partnerFilter
      : null;

  // 프로토 v4는 자유 검색창 없음 — 국가 우선 + Nearest 정렬로 대체. 파트너 필터만.
  const filtered = useMemo(
    () =>
      effectivePartnerFilter
        ? scopeVenues.filter((loc) => loc.partnerId === effectivePartnerFilter)
        : scopeVenues,
    [scopeVenues, effectivePartnerFilter],
  );

  // always-Nearest: userLoc(정밀) ?? 국가 매장 중심에서 haversine 오름차순.
  const sortCenter = useMemo<LatLng | null>(
    () => userLoc ?? countryCentroid(filtered),
    [userLoc, filtered],
  );
  const sortedVenues = useMemo(() => {
    if (!sortCenter) return filtered;
    return [...filtered].sort((a, b) => {
      const da =
        typeof a.lat === "number" && typeof a.lng === "number"
          ? haversineKm(sortCenter, { lat: a.lat, lng: a.lng })
          : Infinity;
      const db =
        typeof b.lat === "number" && typeof b.lng === "number"
          ? haversineKm(sortCenter, { lat: b.lat, lng: b.lng })
          : Infinity;
      return da - db;
    });
  }, [filtered, sortCenter]);

  // 거리 라벨은 정밀 좌표(userLoc)가 있을 때만. 국가 중심 기준 거리는 오해 소지라 미표시.
  const distances = useMemo<Record<string, number>>(() => {
    if (!userLoc) return {};
    const out: Record<string, number> = {};
    for (const loc of sortedVenues) {
      if (typeof loc.lat === "number" && typeof loc.lng === "number") {
        out[loc.id] = haversineKm(userLoc, { lat: loc.lat, lng: loc.lng });
      }
    }
    return out;
  }, [userLoc, sortedVenues]);

  // 마커/행 선택 시 모바일 시트 내부에서 해당 행 노출(§7-5). 동기 scrollTop 만 유효
  // (제스처 레이어가 rAF/native smooth 를 0으로 리셋). peek면 half 승격 후 트랜지션(≈280ms) 뒤 스크롤.
  useEffect(() => {
    if (!isMobile || !mapFull || !selectedId) return;
    const reveal = () => {
      const scroller = sheetContentRef.current;
      const row = sheetRowRefs.current[selectedId];
      if (!scroller || !row) return;
      const sRect = scroller.getBoundingClientRect();
      const rRect = row.getBoundingClientRect();
      scroller.scrollTop += rRect.top - sRect.top - 12;
    };
    if (sheetSnap === "peek") {
      promoteSheet();
      const id = setTimeout(reveal, 300);
      return () => clearTimeout(id);
    }
    reveal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, isMobile]);

  // canonical/hreflang 은 전역 Header(SEO 시스템)가 이미 쿼리 제외로 방출한다. 여기서 중복 금지.
  // ?country= 는 SSG 페이지의 공유/딥링크용 쿼리일 뿐 — 크롤 HTML엔 안 실려 인덱싱 분열 없음.

  const currentCountryName = country
    ? (countries.find((c) => c.code === country)?.name ?? null)
    : null;
  const lastUpdated = data.lastUpdatedAt
    ? new Intl.DateTimeFormat(router.locale ?? "en", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(new Date(data.lastUpdatedAt))
    : null;

  return (
    <PageTemplate>
      {/* 정적 프리렌더 — 띠의 시각 판정을 요청 시각에 못 한다 (#5655 R3 x1). */}
      <PageTemplate.Content hidePromoBanner>
        <div className="mx-auto my-4 flex w-full max-w-screen-checkout-max flex-col items-start gap-5 px-4 laptop:my-16 laptop:gap-8">
          <LocationNoticeBanner />
          <HeroSection totalCount={allFlat.length} />

          <div className="flex w-full flex-col gap-6" ref={filterRef}>
            <PartnerFilter
              partners={scopePartners}
              selectedPartnerId={effectivePartnerFilter}
              onSelectPartner={(id) => {
                setPartnerFilter(id);
                setSelectedId(null);
                analyticsInstance?.whereToTryFilterPartner({ partner: id });
              }}
              countries={countries}
              selectedCountry={country}
              onSelectCountry={(cc) => applyCountry(cc, { user: true })}
              located={userLoc !== null}
              onUseMyLocation={useMyLocation}
              geoError={geoError}
              openToken={countryPickerToken}
              summary={
                // 요약("N venues · M partners in {country}")은 **선택 국가에** 파트너가 2곳
                // 이상일 때만 노출(데스크톱 전용 — PartnerFilter 의 hidden/smd:block).
                // ⚠️ 게이트를 전역 partners 수로 두면 국가 기준 카운트와 축이 어긋나, 노출 파트너가
                // 2곳이 된 순간 모든 국가에서 "N venues · 1 partners in ..." 가 찍힌다.
                // 탭 바와 동일 조건이라 두 표면이 항상 같이 뜨고 같이 사라진다.
                scopePartners.length > 1 && currentCountryName
                  ? t(
                      "{{count}} venues · {{partners}} partners in {{country}}",
                      {
                        count: scopeVenues.length,
                        partners: scopePartners.length,
                        country: currentCountryName,
                      },
                    )
                  : null
              }
            />

            {isMobile ? (
              // 모바일(프로토 mapFull): 기본 = sticky 지도 밴드 + 리스트 일반 흐름.
              // 밴드 탭 → 풀스크린 오버레이 + 바텀시트. minimize/Escape 로 밴드 복귀.
              // gap-3 = 밴드↔리스트 간격 축소(프로토 대비 과했던 갭 정리).
              <div className="flex flex-col gap-3">
                <div
                  ref={stageRef}
                  onClick={mapFull ? undefined : enterMapFull}
                  className={cn(
                    "overflow-hidden",
                    mapFull
                      ? "fixed inset-0 z-[3000] bg-black [overscroll-behavior:none]"
                      : "sticky top-0 z-[5] -mx-4 h-[220px] cursor-pointer shadow-md",
                  )}
                >
                  <ArcadeMap
                    ref={mapHandle}
                    fill
                    // 모바일 밴드=정적(none, 탭으로 확장), 풀스크린=이동 가능(greedy).
                    gesture={mapFull ? "greedy" : "none"}
                    locations={sortedVenues}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    userLoc={userLoc}
                  />
                  {mapFull ? (
                    // 풀스크린 종료 = minimize 셰브론(X 아님, 프로토 계약).
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        exitMapFull();
                      }}
                      aria-label={t("Minimize map") as string}
                      className="absolute right-4 top-4 z-[10] inline-flex size-10 items-center justify-center rounded-full bg-black/60 text-white"
                    >
                      <ChevronToggle up={false} />
                    </button>
                  ) : (
                    // 밴드 탭-확장 어포던스.
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute right-3 top-3 inline-flex size-8 items-center justify-center rounded-lg bg-black/60 text-white"
                    >
                      <ExpandIcon className="size-4" />
                    </span>
                  )}

                  {mapFull && (
                    <div
                      ref={sheetRef}
                      style={sheetStyle}
                      className="flex flex-col rounded-t-2xl border border-coolgray-20 bg-white shadow-[0_-4px_20px_rgba(0,0,0,0.12)]"
                    >
                      {/* 풀스크린 시트 헤더 = 드래그 핸들 grip 만(요약/셰브론 행 없음 — Figma).
                          탭/드래그로 스냅 변경. touch-none 로 스크롤 간섭 방지. */}
                      <div
                        ref={sheetHeaderRef}
                        onPointerDown={onHandlePointerDown}
                        aria-label={t("Drag to resize list / map") as string}
                        className="flex w-full shrink-0 cursor-grab touch-none items-center justify-center py-3"
                      >
                        <span className="h-1 w-10 rounded-full bg-coolgray-30" />
                      </div>
                      <div
                        ref={sheetContentRef}
                        className={cn(
                          "wte-scroll flex-1 px-3",
                          contentScrollable
                            ? "overflow-y-auto"
                            : "overflow-hidden",
                        )}
                      >
                        <LocationList
                          partners={data.partners}
                          locations={sortedVenues}
                          selectedId={selectedId}
                          onSelect={setSelectedId}
                          distances={distances}
                          rowRefs={sheetRowRefs}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* 밴드 모드: 리스트를 지도 밴드 아래 일반 흐름으로(풀스크린 아닐 때만). */}
                {!mapFull && (
                  <LocationList
                    partners={data.partners}
                    locations={sortedVenues}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    distances={distances}
                  />
                )}
              </div>
            ) : (
              // 데스크톱: 프로토 v4 = 1:1 2-pane. 지도 좌측 sticky, 리스트 우측 독립 스크롤.
              <div className="grid w-full grid-cols-1 items-start gap-4 laptop:grid-cols-2">
                <div className="laptop:sticky laptop:top-24">
                  <ArcadeMap
                    locations={sortedVenues}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    userLoc={userLoc}
                  />
                </div>
                <div className="wte-scroll min-w-0 laptop:max-h-[600px] laptop:overflow-y-auto laptop:pr-1.5">
                  <LocationList
                    partners={data.partners}
                    locations={sortedVenues}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    distances={distances}
                  />
                </div>
              </div>
            )}
          </div>

          {/* 푸터(프로토): 좌 스택(Not in {country}? / Choose another country 링크) +
              우 스택(Last updated / 날짜, 우측정렬). 항상 한 행 space-between. */}
          <div className="mt-1.5 flex w-full items-start justify-between gap-3.5 border-t border-coolgray-20 pb-12 pt-[18px] text-xs tracking-[0.4px]">
            <div className="flex flex-col text-coolgray-70">
              {currentCountryName && (
                <>
                  <span>
                    {t("Not in {{country}}?", { country: currentCountryName })}
                  </span>
                  <button
                    type="button"
                    onClick={openCountryPicker}
                    className="w-fit font-600 text-blue-60 underline underline-offset-2"
                  >
                    {t("Choose another country")}
                  </button>
                </>
              )}
            </div>
            {lastUpdated && (
              <div className="flex shrink-0 flex-col text-right text-coolgray-50">
                <span>{t("Last updated")}</span>
                <span>{lastUpdated}</span>
              </div>
            )}
          </div>
        </div>
      </PageTemplate.Content>
    </PageTemplate>
  );
}

function ExpandIcon({ className }: { className?: string }) {
  // 밴드 탭-확장 어포던스(대각 확장 화살표, 프로토 map-expand).
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}

function ChevronToggle({ up }: { up: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn("size-4 transition-transform", up ? "" : "rotate-180")}
    >
      <path d="m6 15 6-6 6 6" />
    </svg>
  );
}

// `_` 프리픽스 키는 **내부 주석용**이다(`_note`, `_source`). props 로 넘기면 그대로
// `__NEXT_DATA__` 에 직렬화돼 공개 페이지 소스에서 검색 가능해진다 — `_source` 에는
// 등재 요청자 이름 같은 사내 정보가 들어간다(예: `request:담당자-날짜`). 그래서 데이터에는
// 남겨 두고(git 은 사내 레포) 클라이언트로 나가는 경계에서만 걷어낸다.
function stripInternalKeys<T extends object>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([k]) => !k.startsWith("_")),
  ) as T;
}

export async function getStaticProps({ locale }: { locale: string }) {
  const raw = arcadeData as unknown as ArcadeDataFile & Record<string, unknown>;
  const data: ArcadeDataFile = {
    lastUpdatedAt: raw.lastUpdatedAt,
    partners: raw.partners
      .filter((p) => EXPOSED_PARTNER_IDS.includes(p.id))
      .map((p) => ({
        ...stripInternalKeys(p),
        locations: p.locations.map(stripInternalKeys),
      })),
  };
  return {
    props: {
      data,
      ...(await serverSideTranslations(locale, ["gnb", "where-to-try"])),
    },
    revalidate: 60 * 60 * 24,
  };
}
